import type { FileUploadProgressCallback } from "@powerhousedao/reactor-browser";
import type {
  Action,
  DocumentOperations,
  Operation,
  PHDocument,
} from "@powerhousedao/shared/document-model";
import { logger } from "document-model";

export async function queueActions(
  document: PHDocument | undefined,
  actionOrActions: Action[] | Action | undefined,
) {
  if (!document) {
    throw new Error("No document provided");
  }
  if (!actionOrActions) {
    throw new Error("No actions provided");
  }
  const actions = Array.isArray(actionOrActions)
    ? actionOrActions
    : [actionOrActions];

  if (actions.length === 0) {
    throw new Error("No actions provided");
  }

  const reactorClient = window.ph?.reactorClient;
  if (!reactorClient) {
    throw new Error("ReactorClient not initialized");
  }

  return await reactorClient.execute(document.header.id, "main", actions);
}

export async function queueOperations(
  documentId: string,
  operationOrOperations: Operation[] | Operation | undefined,
) {
  if (!documentId) {
    throw new Error("No documentId provided");
  }
  if (!operationOrOperations) {
    throw new Error("No operations provided");
  }
  const operations = Array.isArray(operationOrOperations)
    ? operationOrOperations
    : [operationOrOperations];

  if (operations.length === 0) {
    throw new Error("No operations provided");
  }

  const reactorClient = window.ph?.reactorClient;
  if (!reactorClient) {
    throw new Error("ReactorClient not initialized");
  }

  const actions = operations.map((op) => op.action);
  return await reactorClient.execute(documentId, "main", actions);
}

export function deduplicateOperations(
  existingOperations: Record<string, Operation[]>,
  operationsToDeduplicate: Operation[],
) {
  // make a set of all the operation indices for each scope to avoid duplicates
  const operationIndicesByScope = {} as Record<string, Set<number>>;
  for (const scope of Object.keys(existingOperations)) {
    operationIndicesByScope[scope] = new Set(
      existingOperations[scope].map((op) => op.index),
    );
  }

  const newOperations: Operation[] = [];

  for (const operation of operationsToDeduplicate) {
    const scope = operation.action.scope;
    const index = operation.index;
    if (operationIndicesByScope[scope].has(index)) {
      const duplicatedExistingOperation = existingOperations[scope].find(
        (op) => op.index === index,
      );
      const duplicatedNewOperation = newOperations.find(
        (op) => op.index === index,
      );
      console.warn("skipping duplicate operation");
      if (duplicatedExistingOperation) {
        console.warn(
          "duplicate existing operation",
          duplicatedExistingOperation,
        );
      }
      if (duplicatedNewOperation) {
        console.warn("duplicate new operation", duplicatedNewOperation);
      }
      continue;
    }
    newOperations.push(operation);
    operationIndicesByScope[scope].add(index);
  }

  const uniqueOperationIds = new Set<string>();
  const operationsDedupedById: Operation[] = [];

  for (const [scope, operations] of Object.entries(existingOperations)) {
    for (const operation of operations) {
      const id = operation.id;
      if (!id) {
        console.warn("skipping operation with no id", operation);
        continue;
      }
      if (uniqueOperationIds.has(id)) {
        console.warn(
          "skipping existing operation with duplicate id in scope",
          scope,
          operation,
        );
        continue;
      }
      uniqueOperationIds.add(id);
    }
  }

  for (const operation of newOperations) {
    const id = operation.id;
    if (!id) {
      console.warn("skipping operation with no id", operation);
      continue;
    }
    if (uniqueOperationIds.has(id)) {
      console.warn(
        "skipping new operation with duplicate id in scope",
        operation.action.scope,
        operation,
      );
      continue;
    }
    uniqueOperationIds.add(id);
    operationsDedupedById.push(operation);
  }
  return operationsDedupedById;
}

export async function uploadOperations(
  documentId: string,
  allOperations: DocumentOperations,
  pushOperations: (
    documentId: string,
    operations: Operation[],
  ) => Promise<PHDocument | undefined>,
  options?: {
    operationsLimit?: number;
    onProgress?: FileUploadProgressCallback;
  },
) {
  const operationsLimit = options?.operationsLimit || 50;
  const onProgress = options?.onProgress;

  logger.verbose(
    `uploadDocumentOperations(documentId:${documentId}, ops: ${Object.keys(allOperations).join(",")}, limit:${operationsLimit})`,
  );

  // Calculate total operations for progress tracking
  const allOperationsArray = Object.values(allOperations).filter(
    (ops): ops is Operation[] => ops !== undefined,
  );
  const totalOperations = allOperationsArray.reduce(
    (total, operations) => total + operations.length,
    0,
  );
  let uploadedOperations = 0;

  for (const operations of allOperationsArray) {
    for (let i = 0; i < operations.length; i += operationsLimit) {
      logger.verbose(
        `uploadDocumentOperations:for(i:${i}, ops:${operations.length}, limit:${operationsLimit}): START`,
      );
      const chunk = operations.slice(i, i + operationsLimit);
      const operation = chunk.at(-1);
      if (!operation) {
        break;
      }
      const scope = operation.action.scope;

      await pushOperations(documentId, chunk);

      uploadedOperations += chunk.length;

      // Report progress
      if (onProgress) {
        const progress = Math.round(
          (uploadedOperations / totalOperations) * 100,
        );
        onProgress({
          stage: "uploading",
          progress,
          totalOperations,
          uploadedOperations,
        });
      }

      logger.verbose(
        `uploadDocumentOperations:for:waitForUpdate(${documentId}:${scope} rev ${operation.index}): NEXT`,
      );
    }
  }

  logger.verbose(
    `uploadDocumentOperations:for:waitForUpdate(${documentId}): END`,
  );
}
